import React from 'react';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MediaQuery from 'react-responsive';

import { LOGO_ROOT, STATIC_ROOT } from '../../../constants';

import { convertHtmlToReactDomWithParams } from '../../../utils/htmlUtils';
import { distanceInKmWithDecimal } from '../../../components/googlemaps/utils';

import GoogleMaps from '../../../components/googlemaps';
import Form from '../../../components/form/FormComponent';
import SvgComponent from '../../../components/SvgComponent';
import Button from '../../../components/button/ButtonComponent';
import ShopMarker from '../../../components/googlemaps/ShopMarker';
import UserMarker from '../../../components/googlemaps/UserMarker';

import { LoadingComponent } from '../../../components/loading/LoadingComponent';
import TextInputFieldComponent from '../../../components/form/TextInputFieldComponent';

import './ShopsMap.scss';

const hasLocation = ({ location }) => !!location && !!(location.latitude || location.longitude);

const renderColumn = (shops, userPosition, i18n) =>
    shops.map(shop => {
        const {
            id,
            name,
            distance,
            address: { city, labels, postcode }
        } = shop;

        return (
            <li key={id}>
                <span>
                    <b>{name}</b>
                    {userPosition && distance != null && (
                        <span>
                            &nbsp;-&nbsp;<span className="distance">{distance}km</span>
                        </span>
                    )}
                    {!hasLocation(shop) && (
                        <span>
                            &nbsp;-&nbsp;
                            <span className="distance">{i18n.STORE_NOT_LOCATED}</span>
                        </span>
                    )}
                </span>
                <span>
                    {labels.join(' ')} {postcode} {city}
                </span>
            </li>
        );
    });

const SHOPS_LIMIT = 6;

class Search extends React.Component {
    state = {
        inputValue: ''
    };

    componentDidMount() {
        const { defaultValue, onSubmit } = this.props;

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                position => {
                    onSubmit({ lat: position.coords.latitude, lng: position.coords.longitude });
                    this.setState({ inputValue: '' });
                },
                () => {
                    this.setState({ inputValue: defaultValue });
                    this.onSubmit(defaultValue);
                },
                {
                    enableHighAccuracy: true
                }
            );
        } else {
            this.setState({ inputValue: defaultValue });
            this.onSubmit(defaultValue);
        }
    }

    onSubmit = postcode => {
        if (postcode) {
            const { getAddress, onSubmit } = this.props;

            getAddress(postcode)
                .then(userPosition => onSubmit(userPosition))
                .catch(e => {});
        }
    };

    onChange = e => {
        this.setState({ inputValue: e.target.value });
    };

    render() {
        const { inputValue } = this.state;

        return (
            <Form
                form="map"
                classNameInputContainer="searchForm"
                onSubmit={() => this.onSubmit(document.forms.map.zipcode.value)}
                requestSubmit={false}
                errorSubmit={false}
                hideButton={true}>
                <TextInputFieldComponent
                    placeholder="Mon code postal / Ma ville"
                    name="zipcode"
                    value={inputValue}
                    onChange={this.onChange}
                    id="userPosition"
                />
                <Button
                    text={
                        <React.Fragment>
                            <SvgComponent
                                className="loupe"
                                src={`${STATIC_ROOT}/picto-rechercher.svg`}
                                width="19"
                                height="19"
                            />
                            <MediaQuery query={`(min-width: 769px)`}>
                                {this.props.i18n.SEARCH}
                            </MediaQuery>
                        </React.Fragment>
                    }
                    color="main"
                    onClick={e => {
                        e.preventDefault();
                        this.onSubmit(document.forms.map.zipcode.value);
                    }}
                />
            </Form>
        );
    }
}

class ShopsMap extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            shopsLimit: SHOPS_LIMIT,
            userPosition: null
        };
    }

    onShowMore = (type, step) => () => {
        this.setState({
            [`${type}Limit`]: this.state[`${type}Limit`] + step
        });
    };

    render() {
        const { user = {}, partner = {}, shops, status, error, i18n } = this.props;
        const { shopsLimit, userPosition } = this.state;

        const locatedShops = shops.filter(hasLocation);
        const unlocatedShops = shops.filter(shop => !hasLocation(shop));

        const origin = userPosition
            ? { latitude: userPosition.lat, longitude: userPosition.lng }
            : (locatedShops[0] || {}).location;

        const sortedShops = locatedShops
            .map(shop => ({
                ...shop,
                distance: distanceInKmWithDecimal(origin, shop.location),
                userPosition
            }))
            .sort((a, b) => a.location.latitude - b.location.latitude)
            .map((shop, index) => ({ ...shop, zindex: index }))
            .sort((a, b) => a.distance - b.distance)
            .concat(unlocatedShops)
            .slice(0, shopsLimit);

        const total = shops && shops.length >= 0 ? shops.length : 0;
        const length = sortedShops.length;
        const nbShopsInColumn = length % 2 === 0 ? length / 2 : length / 2 + 1;

        if (!status && isEmpty(error)) {
            return (
                <div className="mapBloc">
                    <GoogleMaps>
                        {({ Map, getAddress }) => (
                            <React.Fragment>
                                <h3>
                                    {convertHtmlToReactDomWithParams(
                                        i18n.PARTICIPATING_STORES,
                                        partner.name
                                    )}
                                </h3>

                                <div className="smallBloc">
                                    <div>
                                        <Search
                                            defaultValue={user.postcode}
                                            getAddress={getAddress}
                                            onSubmit={userPosition => {
                                                this.setState({ userPosition });
                                            }}
                                            i18n={i18n}
                                        />

                                        <Map
                                            center={{
                                                lat: 46.227638, // Centre de la France
                                                lng: 2.213749
                                            }}
                                            zoom={5}
                                            options={{
                                                disableDefaultUI: true,
                                                clickableIcons: false
                                            }}
                                            style={{
                                                height: '350px',
                                                width: '100%'
                                            }}>
                                            {!!userPosition && (
                                                <UserMarker position={userPosition} />
                                            )}
                                            {sortedShops.filter(hasLocation).map((shop, index) => (
                                                <ShopMarker
                                                    {...shop}
                                                    key={shop.id}
                                                    position={{
                                                        lat: shop.location.latitude,
                                                        lng: shop.location.longitude
                                                    }}
                                                    index={index + 1}
                                                    logo={`${LOGO_ROOT}/${partner.logo}`}
                                                    discount={
                                                        partner.mostDisplayableDiscountOffline
                                                    }
                                                />
                                            ))}
                                        </Map>
                                        <div className="user-marker-container">
                                            <div className="MarkerUserWrapper">
                                                <SvgComponent
                                                    src={`${STATIC_ROOT}/maps/pin_here.svg`}
                                                    width="34"
                                                    height="44"
                                                    aria-hidden="true"
                                                    focusable="false"
                                                />
                                            </div>
                                            <div className="here-text">{i18n.HERE}</div>
                                        </div>

                                        <p className="total">
                                            {convertHtmlToReactDomWithParams(
                                                i18n.TOTAL_STORES,
                                                total
                                            )}
                                        </p>

                                        {sortedShops.length > 0 && (
                                            <MediaQuery maxWidth={'768px'}>
                                                <ol className="shops">
                                                    <div className="column">
                                                        {renderColumn(
                                                            sortedShops,
                                                            userPosition,
                                                            i18n
                                                        )}
                                                    </div>
                                                </ol>
                                            </MediaQuery>
                                        )}

                                        {sortedShops.length > 0 && (
                                            <MediaQuery minWidth={'769px'}>
                                                <ol className="shops">
                                                    <div className="column">
                                                        {renderColumn(
                                                            sortedShops.slice(0, nbShopsInColumn),
                                                            userPosition,
                                                            i18n
                                                        )}
                                                    </div>
                                                    <div className="column">
                                                        {renderColumn(
                                                            sortedShops.slice(
                                                                nbShopsInColumn,
                                                                length
                                                            ),
                                                            userPosition,
                                                            i18n
                                                        )}
                                                    </div>
                                                </ol>
                                            </MediaQuery>
                                        )}

                                        <div style={{ textAlign: 'center' }}>
                                            {shopsLimit < shops.length && (
                                                <Button
                                                    className="showMore"
                                                    color="main"
                                                    text={i18n.SEE_MORE_STORE}
                                                    onClick={this.onShowMore('shops', SHOPS_LIMIT)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </GoogleMaps>
                </div>
            );
        } else if (status && isEmpty(error)) {
            return <LoadingComponent />;
        } else {
            return <span>{error}</span>;
        }
    }
}

ShopsMap.propTypes = {
    user: PropTypes.object,
    partner: PropTypes.object,
    shops: PropTypes.array,
    status: PropTypes.bool,
    error: PropTypes.string
};

const mapStateToProps = state => ({
    i18n: state.i18n.page.MERCHANTCARD
});

export default connect(mapStateToProps)(ShopsMap);



// WEBPACK FOOTER //
// ./src/components/pages/merchantcard/ShopsMap.js